import React, { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import AsyncSelect from "react-select/async";
import { Alert, Avatar, Button, Checkbox } from "@opendate-app/setlist";

import ColorPicker from "../../../../shared/ColorPicker";
import "../landingPageColorPicker.css";
import BlurTextInput from "../BlurTextInput";
import FullEditor from "./FullEditor";
import { useLandingPage } from "../LandingPageBuilderContainer";

const FIELD_OPTIONS = [
  { key: "first", label: "First name" },
  { key: "phone", label: "Phone (SMS)" },
  { key: "zip", label: "Zip code" },
  { key: "birthday", label: "Birthday" },
];

const styles = {
  stack: { display: "flex", flexDirection: "column", gap: 24, marginTop: 24 },
  section: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--sl-text-secondary)" },
  hint: { fontSize: 13, color: "var(--sl-text-tertiary)" },
  note: { fontSize: 14, color: "var(--sl-text-primary)" },
  swatches: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  swatch: (color, selected) => ({
    width: 32,
    height: 32,
    borderRadius: 999,
    cursor: "pointer",
    background: color,
    border: "2px solid var(--sl-bg-primary)",
    boxShadow: selected ? `0 0 0 2px ${color}` : "0 0 0 1px var(--sl-border-primary)",
  }),
  custom: (selected) => ({
    width: 190,
    borderRadius: 8,
    boxShadow: selected ? "0 0 0 2px var(--sl-border-brand)" : "none",
  }),
  logoRow: { display: "flex", alignItems: "center", gap: 12, fontSize: 14, color: "var(--sl-text-secondary)" },
};

const ACCENT_SAVE_DELAY = 500;
const EVENT_SEARCH_DELAY = 300;
const NO_EVENT = { value: "none", label: "No specific event" };
const eventOption = (event) => ({ value: event.id, label: `${event.title} · ${event.date_label}` });

const MENU_PORTAL = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

const normalizeHex = (value) => {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || "").trim());
  if (!match) return null;
  const digits = match[1].length === 3 ? match[1].split("").map((digit) => digit + digit).join("") : match[1];
  return `#${digits.toLowerCase()}`;
};

const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join("");

const ContentStep = ({ fullEditor, setFullEditor }) => {
  const { page, props, venueData, searchEvents, savePage } = useLandingPage();
  const settings = page.settings || {};
  const locked = Boolean(settings.full_editor);
  const fields = settings.fields || [];
  const inquiryFields = props.inquiryFields?.[page.template_key];
  const venue = props.venueOwnerships.find((item) => item.id === page.venue_ownership_id);
  const venueName = venue?.name || page.venue_name || "";

  const [pickedEvent, setPickedEvent] = useState(null);
  const eventDefaults = useMemo(() => [NO_EVENT, ...venueData.events.map(eventOption)], [venueData.events]);
  const loadEvents = useMemo(() => debounce((input, callback) => {
    searchEvents(page.venue_ownership_id, input).then((events) => callback(events.map(eventOption)));
  }, EVENT_SEARCH_DELAY), [searchEvents, page.venue_ownership_id]);
  useEffect(() => () => loadEvents.cancel(), [loadEvents]);

  const selectedEvent = page.calendar_event_id == null ? NO_EVENT
    : pickedEvent?.value === page.calendar_event_id ? pickedEvent
      : eventDefaults.find((option) => option.value === page.calendar_event_id)
        || (venueData.selected_event?.id === page.calendar_event_id ? eventOption(venueData.selected_event) : null);

  const pickEvent = (option) => {
    setPickedEvent(option);
    savePage({ calendar_event_id: option.value === NO_EVENT.value ? null : option.value });
  };

  const accentIsCustom = normalizeHex(settings.accent) !== null;
  const [customAccent, setCustomAccent] = useState(accentIsCustom ? settings.accent : "");
  const saveAccent = useRef();
  const lastSentAccent = useRef(settings.accent);
  saveAccent.current = (hex) => {
    lastSentAccent.current = hex;
    return savePage({ settings: { accent: hex } });
  };
  const debouncedSaveAccent = useMemo(() => debounce((hex) => saveAccent.current(hex), ACCENT_SAVE_DELAY), []);
  const presetHexes = useMemo(() => Object.values(props.accents), [props.accents]);

  useEffect(() => () => debouncedSaveAccent.flush(), [debouncedSaveAccent]);
  useEffect(() => {
    if (settings.accent === lastSentAccent.current) return;
    debouncedSaveAccent.cancel();
    lastSentAccent.current = settings.accent;
    setCustomAccent(normalizeHex(settings.accent) ? settings.accent : "");
  }, [settings.accent, debouncedSaveAccent]);

  const changeCustomAccent = (value) => {
    if (locked) return;
    setCustomAccent(value);
    const hex = normalizeHex(value);
    if (hex) debouncedSaveAccent(hex);
    else debouncedSaveAccent.cancel();
  };

  const choosePreset = (name) => {
    debouncedSaveAccent.cancel();
    setCustomAccent("");
    lastSentAccent.current = name;
    saveSetting("accent")(name);
  };

  if (fullEditor) return <FullEditor onDone={() => setFullEditor(false)} />;

  const saveSetting = (key) => (value) => savePage({ settings: { [key]: value } });

  const toggleField = (key, checked) => {
    const next = checked ? [...fields, key] : fields.filter((field) => field !== key);
    savePage({ settings: { fields: next } });
  };

  return (
    <div style={styles.stack}>
      {locked && (
        <Alert color="warning" title="You've edited this page in the full editor, so these fields are locked. Keep editing there." />
      )}

      {!inquiryFields && (
        <div style={styles.section}>
          <label style={styles.label} htmlFor="landing-page-event">Event</label>
          <AsyncSelect
            key={page.venue_ownership_id}
            inputId="landing-page-event"
            name="landing-page-event"
            placeholder="Search upcoming events…"
            isDisabled={locked}
            cacheOptions
            defaultOptions={eventDefaults}
            loadOptions={loadEvents}
            noOptionsMessage={({ inputValue }) => (inputValue ? "No upcoming events match" : "No upcoming events")}
            value={selectedEvent}
            onChange={pickEvent}
            {...MENU_PORTAL}
          />
        </div>
      )}

      <BlurTextInput label="Headline" disabled={locked} value={settings.headline} onSave={saveSetting("headline")} />
      <BlurTextInput label="Description" multiline rows={3} disabled={locked} value={settings.subhead} onSave={saveSetting("subhead")} />
      <BlurTextInput label="Button label" disabled={locked} value={settings.cta} onSave={saveSetting("cta")} />

      <div style={styles.section}>
        <div style={styles.label}>What do you want to ask for?</div>
        {inquiryFields ? (
          <>
            <div style={styles.note}>This blueprint asks for: {inquiryFields.map((field) => field.label).join(", ")}</div>
            <div style={styles.hint}>Inquiry forms always ask these questions so your events team gets what they need to follow up.</div>
          </>
        ) : (
          <>
            <Checkbox label="Email" checked disabled />
            {FIELD_OPTIONS.map((option) => (
              <Checkbox
                key={option.key}
                label={option.label}
                disabled={locked}
                checked={fields.includes(option.key)}
                onChange={(event) => toggleField(option.key, event.target.checked)}
              />
            ))}
            <div style={styles.hint}>Fewer fields means more signups. Email is always included.</div>
          </>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.label}>Accent color</div>
        <div style={styles.swatches}>
          {Object.entries(props.accents).map(([name, color]) => (
            <button
              key={name}
              type="button"
              aria-label={name}
              aria-pressed={settings.accent === name}
              disabled={locked}
              style={styles.swatch(color, settings.accent === name)}
              onClick={() => choosePreset(name)}
            />
          ))}
          <div role="group" aria-label="Custom accent color" style={styles.custom(accentIsCustom)}>
            <ColorPicker
              value={customAccent}
              onChange={changeCustomAccent}
              presetColors={presetHexes}
              inputPlaceholder="Custom"
              disabled={locked}
              position="top left"
              className="lp-color-picker"
            />
          </div>
        </div>
      </div>

      <div style={styles.logoRow}>
        <Avatar size="md" src={venue?.logoUrl || undefined} initials={initialsFor(venueName)} alt={venueName} />
        <span>{venue?.logoUrl ? `From ${venueName} venue settings` : "No logo yet. Using initials."}</span>
      </div>

      <div>
        <Button variant="secondary" onClick={() => setFullEditor(true)}>Open full editor</Button>
      </div>
    </div>
  );
};

export default ContentStep;
